import React, { useEffect, useState, useContext } from "react";
import JiraProjectsComponentPage from "../../../common-components/JiraProjectsComponentPage";
import Epics from "../../../common-components/Epics";
import {
  getAllEpics,
  getAllJiraFields,
  getAllProjects,
  getAllSprints,
  getFilteredBugs,
  getJiraStatusesForFilters,
  getProjectVersions,
  getUnassignedStories,
  getUserStoriesUnderEpic,
  getUserStoriesUnderSprint,
} from "../../../../services/webServices";
import ImportUserStories from "../../../common-components/ImportUserStories";
import BugsFiltersPage from "../../../common-components/BugsFiltersPage";
import { useNavigate } from "react-router-dom";
import LoadingModal from "../../../common-components/LoadingModal";
import MessagePopup from "../../../popup-components/MessagePopup";
import SprintSelectionPage from "../../../common-components/SprintSelection";
import { LoadingModalContext, MessagePopupContext } from "../../ServicePage";
// import FieldMapper from "../../../common-components/FieldMapper";

const JiraSelectionPage = ({ issueType, setJiraPageOpen }) => {
  const navigate = useNavigate();
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [epicsList, setEpicsList] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [sprintsList, setSprintsList] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [releaseVersionsList, setReleaseVersionsList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [unassignedStories, setUnassignedStories] = useState([]);
  const [selectedUnassignedUserStories, setSelectedUnassignedUserStories] =
    useState([]);
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);
  const [userStoriesList, setUserStoriesList] = useState([]);
  const [selectedUserStories, setSelectedUserStories] = useState([]);
  const [selectedBugFilters, setSelectedBugFilters] = useState({});
  const [proceedingMap, setProceedingMap] = useState(null);
  const [loader, setLoader] = useState(false);
  const [importLoading, setImportLoading] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const { setLoadingState } = useContext(LoadingModalContext);

  useEffect(() => {
    const fetchProjects = async () => {
      Promise.resolve().then(() => setImportLoading("Projects")); // Next microtask

      try {
        const getProjectsFromJiraAPI = await getAllProjects();
        setProjectsList(getProjectsFromJiraAPI.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
        const errMsg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch projects. Please try again.";
        setMessage(errMsg);
        setMessagePopup(true);
      } finally {
        setImportLoading(null); // Clear state after fetch completes
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      Promise.resolve().then(() => setImportLoading("Epics And Sprints")); // Show loading immediately

      const selectedProjectKey = selectedProject.key;

      const getAllEpicsListFromSelectedProject = () =>
        getAllEpics(selectedProjectKey)
          .then((res) => setEpicsList(res.data))
          .catch((error) => {
            console.error("Error fetching epics:", error);
            const errMsg =
              error.response?.data?.message ||
              error.response?.data?.error ||
              "Failed to fetch epics. Please check your Jira configuration and try again.";
            setMessage(errMsg);
            // setMessagePopup(true);
            setEpicsList([]); // Set empty array on error
          });
      const getSprintsFromSelectedProjects = () =>
        getAllSprints(selectedProjectKey).then((res) =>
          setSprintsList(res.data)
        );
      const getAllUnassignedUserStories = () =>
        getUnassignedStories(selectedProject.name,selectedProjectKey, issueType="Story")
          .then((res) => setUnassignedStories(res.data))
          .catch((error) => {
            console.error("Error fetching unassigned stories:", error);
            const errMsg =
              error.response?.data?.message ||
              error.response?.data?.error ||
              "Failed to fetch unassigned stories. Please check your Jira configuration and try again.";
            setMessage(errMsg);
            // setMessagePopup(true);
            setUnassignedStories([]); // Set empty array on error
          });
      const getAllReleaseVersions = () =>
        getProjectVersions(selectedProjectKey).then((res) =>
          setReleaseVersionsList(res.data)
        );
      const getAllStatusList = () =>
        getJiraStatusesForFilters({
          projectKey: selectedProjectKey,
          issueType:
            issueType === "Bugs"
              ? "Bug"
              : issueType === "User Stories"
                ? "Story"
                : issueType,
        }).then((res) => {
          setStatusList(res.data);
        });
      const fetchData = async () => {
        try {
          if (issueType === "User Stories" || issueType === "Story") {
            await Promise.all([
              getAllEpicsListFromSelectedProject(),
              getSprintsFromSelectedProjects(),
              getAllUnassignedUserStories(),
            ]);
          }
          if (issueType === "Bugs") {
            await Promise.all([
              getSprintsFromSelectedProjects(),
              getAllReleaseVersions(),
              getAllStatusList(),
            ]);
          }
          if (issueType === "Sprints") {
            await Promise.all([getSprintsFromSelectedProjects()]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setImportLoading(null); // Only sets loading to null AFTER completion
        }
      };

      fetchData();
    }
  }, [selectedProject, issueType]);

  useEffect(() => {
    const getStoriesUnderEpics = async () => {
      try {
        if (selectedEpic && Object.keys(selectedEpic).length > 0) {
          Promise.resolve().then(() => setImportLoading("User Stories")); // Next microtask
          const epicKey = selectedEpic?.key;
          console.log(epicKey)
          const getUserStories = await getUserStoriesUnderEpic(
            selectedProject.name,
            epicKey,
            issueType='Story',
          );
          setUserStoriesList(getUserStories.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setImportLoading(null);
      }
    };
    getStoriesUnderEpics();
  }, [selectedEpic]);

  useEffect(() => {
    const getStoriesUnderSprints = async () => {
      try {
        if (selectedSprint && Object.keys(selectedSprint).length > 0) {
          Promise.resolve().then(() => setImportLoading("User Stories")); // Next microtask
          const sprintId = selectedSprint?.id;
          const getUserStories = await getUserStoriesUnderSprint(
            selectedProject.name,
            sprintId,
            issueType="Story"
          );
          setUserStoriesList(getUserStories.data);
          // setJiraPageOpen(false); // Close the Jira page after fetching data
        }
      } catch (error) {
        setErrorMessage(error.response?.data?.message);
      } finally {
        setImportLoading(null);
      }
    };

    getStoriesUnderSprints();
  }, [selectedSprint]);

  useEffect(() => {
    if (issueType === "Sprints" && userStoriesList.length > 0) {
      navigate("/dashboard/test-plan", {
        state: {
          userStories: userStoriesList,
          sprintDetails: selectedSprint.name,
          projectName: selectedProject.name,
          startDate: selectedSprint.startDate,
          endDate: selectedSprint.endDate,
        },
        replace: true,
      });
      setJiraPageOpen(false);
    }
  }, [userStoriesList]);

  useEffect(() => {
    const getBugReportsByFilters = async () => {
      try {
        //setLoader(true);
        setLoadingState({
          open: true,
          message: "Fetching Bug Reports",
          progress: null,
        });
        const reqBody = {
          ...selectedBugFilters,
          ReleaseVersion: selectedBugFilters["Release version"],
          projectKey: selectedProject.key,
          projectName:selectedProject.name,
          issueType:'Bug'
        };
        const getBugsByFilter = await getFilteredBugs(reqBody);
        navigate("/dashboard/multi-bugreports/input", {
          state: {
            bugDetails: getBugsByFilter.data,
            almConfig: {
              fieldMapping: proceedingMap,
              projectName: selectedProject.name,
              issueType,
              toolName: "Jira",
            },
          },
        });
      } catch (error) {
        console.error("error", error);
        setSelectedBugFilters({});
        setMessagePopup(true);
        setMessage(
          error.response?.data?.message ||
            "Failed to fetch bug reports. Please try again."
        );
      } finally {
        //setLoader(false);
        // setJiraPageOpen(false);
        setLoadingState({ open: false, message: "", progress: null });
      }
    };

    if (Object.entries(selectedBugFilters).length > 0) {
      getBugReportsByFilters();
    }
  }, [selectedBugFilters]);

  const handleAvailableFields = async () => {
    const payload = {
      projectName: selectedProject.key,
      issueType,
    };

    try {
      const availableFields = await getAllJiraFields(payload);
      return availableFields.data;
    } catch (error) {
      console.error("Error fetching available fields:", error);
    }
  };

  return (
    <div>
      {!selectedProject && (
        <JiraProjectsComponentPage
          issueType={issueType}
          taskType={"import"}
          projectsLoading={importLoading === "Projects"}
          setOpenProjectPage={setJiraPageOpen}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          projectsList={projectsList}
        />
      )}

      {/* {!!selectedProject && !proceedingMap && (
        <FieldMapper
          setProceedingMap={setProceedingMap}
          setALMModalOpen={setJiraPageOpen}
          setPreviousSelection={setSelectedProject}
          toolName={"Jira"}
          projectName={selectedProject.name}
          issueType={issueType}
          taskType="Import"
          getAvailableFields={handleAvailableFields}
        />
      )} */}

      {issueType === "User Stories" && selectedProject && (
        <>
          {!selectedEpic && !selectedSprint && (
            <Epics
              epicsAndSprintsLoading={importLoading === "Epics And Sprints"}
              setSelectedParent={setSelectedProject}
              setEpic={setSelectedEpic}
              setSprint={setSelectedSprint}
              epicsList={epicsList}
              setEpicsList={setEpicsList}
              sprintsList={sprintsList}
              setSprintsList={setSprintsList}
              unassignedStories={unassignedStories}
              setUnassignedStories={setUnassignedStories}
              selectedUnassignedUserStories={selectedUnassignedUserStories}
              setselectedUnassignedUserStories={
                setSelectedUnassignedUserStories
              }
              setImportOpen={setJiraPageOpen}
              selectedProjectName={selectedProject?.name}
              almConfig={{
                fieldMapping: proceedingMap,
                projectName: selectedProject.name,
                toolName: "Jira",
                issueType,
              }}
            />
          )}

          {selectedEpic && (
            <ImportUserStories
              almConfig={{
                fieldMapping: proceedingMap,
                projectName: selectedProject.name,
                toolName: "Jira",
                issueType,
              }}
              userStoriesLoading={importLoading === "User Stories"}
              setSelectedParent={setSelectedEpic}
              emptyParent={() => setSelectedEpic(null)}
              userStoriesList={userStoriesList}
              setUserStoriesList={setUserStoriesList}
              selectedUserStories={selectedUserStories}
              setSelectedUserStories={setSelectedUserStories}
              setImportPageOpen={setJiraPageOpen}
              selectedProjectName={selectedProject?.name}
            />
          )}

          {selectedSprint && (
            <ImportUserStories
              almConfig={{
                fieldMapping: proceedingMap,
                projectName: selectedProject.name,
                toolName: "Jira",
                issueType,
              }}
              userStoriesLoading={importLoading === "User Stories"}
              setSelectedParent={setSelectedSprint}
              emptyParent={() => setSelectedSprint(null)}
              userStoriesList={userStoriesList}
              setUserStoriesList={setUserStoriesList}
              selectedUserStories={selectedUserStories}
              setSelectedUserStories={setSelectedUserStories}
              setImportPageOpen={setJiraPageOpen}
            />
          )}
        </>
      )}

      {issueType === "Bugs" && selectedProject && (
        <>
          {selectedProject && (
            <BugsFiltersPage
              filters={{
                Sprint: sprintsList.map((eachSprint) => ({
                  name: eachSprint.name,
                  value: eachSprint.id,
                })),
                "Release version": releaseVersionsList.map((eachVersion) => ({
                  name: eachVersion.name,
                  value: eachVersion.id,
                })),
                Status: statusList.map((eachStatus) => ({
                  name: eachStatus,
                  value: eachStatus,
                })),
                Priority: [
                  { name: "Low", value: "Low" },
                  { name: "Medium", value: "Medium" },
                  { name: "High", value: "High" },
                ],
              }}
              setSprintsList={setSprintsList}
              setOpenFiltersPage={setJiraPageOpen}
              setParent={setSelectedProject}
              setFilterSubmit={setSelectedBugFilters}
              projectKey={selectedProject.key}
            />
          )}
        </>
      )}

      {issueType === "Sprints" && selectedProject && !!proceedingMap && (
        <>
          {!selectedSprint && (
            <SprintSelectionPage
              issueType={issueType}
              teamsLoading={importLoading === "Epics And Sprints"}
              setTeamsList={setSprintsList}
              teamsList={sprintsList}
              selectedTeam={selectedSprint}
              setSelectedTeam={setSelectedSprint}
              setImportPageOpen={setJiraPageOpen}
              setSelectedProject={setSelectedProject}
            />
          )}
          {importLoading === "User Stories" && (
            <LoadingModal open={importLoading === "User Stories"} />
          )}
        </>
      )}

      {loader && <LoadingModal open={loader} />}
      {/* {!!errorMessage && (
        <MessagePopup
          modalOpen={!!errorMessage}
          message={errorMessage}
          okayHandler={() => {
            setErrorMessage(null);
            setJiraPageOpen(false);
          }}
        />
      )} */}
    </div>
  );
};

export default JiraSelectionPage;
